/**
 * ProviderEditorTabs Component (T090)
 * Tab container that switches between Information and Preview tabs
 * 
 * Features:
 * - Information tab: Collapsible sections for editing all provider fields
 * - Preview tab: Exact preview of public ProviderDetails page
 * - Tab switching with keyboard support
 * - Form state shared between tabs
 * - Unsaved changes indication in Preview tab
 */

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { InformationTab } from './InformationTab';
import { PreviewTab } from './PreviewTab';
import type { Control, FieldErrors, UseFormSetValue } from 'react-hook-form';
import type { ProviderEditorFormData } from './schemas';

type ProviderEditorTabsProps = {
  /** Form control from React Hook Form */
  control: Control<ProviderEditorFormData>;
  
  /** Form validation errors */
  errors: FieldErrors<ProviderEditorFormData>;
  
  /** Current form data for preview */
  formData: ProviderEditorFormData;
  
  /** Whether form has unsaved changes */
  isDirty: boolean;
  
  /** User ID for read-only display (edit mode only) */
  userId?: string;
  
  /** Kinde ID for read-only display (edit mode only) */
  kindeId?: string;

  /** Embedded intake form URL for read-only display (Feature 103 pilot).
   *  Set only via manual SQL backfill — never editable in this form. */
  intakeFormUrl?: string | null;

  /** Provider id (ServiceProvider.id) — passed to surfaces that load
   *  provider-scoped resources independently of the form, e.g. Feature 073 PDFs. */
  providerId?: string;
  
  /** Callback to update individual fields inline in Preview tab */
  onFieldUpdate: (fieldName: string, value: string) => Promise<void>;

  /** React Hook Form setValue for programmatic field updates */
  setValue: UseFormSetValue<ProviderEditorFormData>;

  /** Server validation messages (e.g. specialties[0].coverageStates: ...) */
  serverFieldErrors?: string[];
};

/**
 * ProviderEditorTabs Component
 * 
 * Main tab container for provider editor
 * Provides two views:
 * 1. Information: Form view with collapsible sections
 * 2. Preview: Exact preview of public provider profile
 * 
 * @example
 * <ProviderEditorTabs
 *   control={form.control}
 *   errors={form.formState.errors}
 *   formData={form.getValues()}
 *   isDirty={form.formState.isDirty}
 * />
 */
export function ProviderEditorTabs({
  control,
  errors,
  formData,
  isDirty,
  userId,
  kindeId,
  intakeFormUrl,
  providerId,
  onFieldUpdate,
  setValue,
  serverFieldErrors = [],
}: ProviderEditorTabsProps) {
  return (
    <Tabs defaultValue="information" className="w-full">
      {/* Tab List */}
      <TabsList className="w-full justify-start border-b border-gray-200 bg-transparent p-0">
        <TabsTrigger
          value="information"
          className="rounded-none border-b-2 border-transparent px-6 py-3 data-[state=active]:border-[#026857] data-[state=active]:bg-transparent data-[state=active]:text-[#026857]"
        >
          Information
        </TabsTrigger>
        <TabsTrigger
          value="preview"
          className="rounded-none border-b-2 border-transparent px-6 py-3 data-[state=active]:border-[#026857] data-[state=active]:bg-transparent data-[state=active]:text-[#026857]"
        >
          Preview
        </TabsTrigger>
      </TabsList>

      {/* Information Tab Content */}
      <TabsContent value="information" className="mt-6">
        <InformationTab
          control={control}
          errors={errors}
          userId={userId}
          kindeId={kindeId}
          intakeFormUrl={intakeFormUrl}
          providerId={providerId}
          isDirty={isDirty}
          setValue={setValue}
          serverFieldErrors={serverFieldErrors}
        />
      </TabsContent>

      {/* Preview Tab Content */}
      <TabsContent value="preview" className="mt-6">
        <PreviewTab
          formData={formData}
          isDirty={isDirty}
          onFieldUpdate={onFieldUpdate}
          intakeFormUrl={intakeFormUrl}
        />
      </TabsContent>
    </Tabs>
  );
}

